import { useState, useEffect } from "react";
import { B, fonts, Input, Btn } from "../../../components/UI";
import GSTSegmentHint from "../../../components/GSTSegmentHint";
import TransportSelect from "../../../components/TransportSelect";
import { getGSTValidationMessage, getMobileValidationMessage } from "../../../utils/validation";

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana",
  "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
  "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

// Customer create/edit/view form extracted from Dashboard.
export default function CustomerForm({ initial, onDone, onSubmit, saving, readOnly = false, showToast, showFirmName = true, transports = [] }) {
  const [form, setForm] = useState(initial || {
    customer_name: "",
    address: "",
    transport_name: "",
    transport_id: "",
    transport_gst: "",
    city: "",
    state: "",
    pincode: "",
    mobile_number: "",
    firm_name: "",
    agent_name: "",
    gst_number: "",
    status: "active",
  });
  const [gstError, setGstError] = useState("");
  const [gstFocused, setGstFocused] = useState(false);

  useEffect(() => {
    setForm(initial || {
      customer_name: "",
      address: "",
      transport_name: "",
      transport_id: "",
      transport_gst: "",
      city: "",
      state: "",
      pincode: "",
      mobile_number: "",
      firm_name: "",
      agent_name: "",
      gst_number: "",
      status: "active",
    });
  }, [initial]);

  const toTitleCase = (value) =>
    String(value || "").replace(/\b\w/g, char => char.toUpperCase());

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = () => {
    if (readOnly) return;
    if (!String(form.customer_name || "").trim()) {
      showToast?.("Customer name is required", B.red);
      return;
    }
    const mobileMessage = getMobileValidationMessage(form.mobile_number);
    if (mobileMessage) {
      showToast?.(mobileMessage, B.red);
      return;
    }
    const gstMessage = getGSTValidationMessage(form.gst_number);
    if (gstMessage) {
      setGstError(gstMessage);
      showToast?.(gstMessage, B.red);
      return;
    }
    onSubmit?.(form);
  };

  return (
    <div style={{ background: B.orangeFaint, border: `1.5px solid ${B.orangeMid}`, borderRadius: 14, padding: 24, marginBottom: 24 }}>
      <div style={{ fontWeight: 800, fontSize: 16, color: B.text, marginBottom: 20, fontFamily: fonts.adminDisplay }}>
        {readOnly ? "👁️ View Customer" : initial ? "✏️ Edit Customer" : "➕ Add Customer"}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 18px" }}>
        <Input label="Customer Name" value={form.customer_name} onChange={e => set("customer_name", toTitleCase(e.target.value))} disabled={readOnly} />
        {showFirmName && <Input label="Firm Name" value={form.firm_name} onChange={e => set("firm_name", toTitleCase(e.target.value))} disabled={readOnly} />}
        <Input label="Mobile Number" value={form.mobile_number} onChange={e => set("mobile_number", e.target.value)} disabled={readOnly} />
        <Input label="City" value={form.city} onChange={e => set("city", toTitleCase(e.target.value))} disabled={readOnly} />
        <Input label="Pincode" value={form.pincode || ""} onChange={e => set("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))} disabled={readOnly} />
        <div>
          <label style={{ display: "block", fontSize: 10, fontWeight: 600, color: B.textMid, marginBottom: 8, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: fonts.body }}>
            State
          </label>
          <select
            value={form.state || ""}
            onChange={e => set("state", e.target.value)}
            disabled={readOnly}
            style={{ background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 12, color: B.text, fontFamily: fonts.body, outline: "none", width: "100%" }}
          >
            <option value="">Select state</option>
            {INDIAN_STATES.map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
        </div>
        <Input label="Agent Name" value={form.agent_name || ""} onChange={e => set("agent_name", toTitleCase(e.target.value))} disabled={readOnly} />
        <div onFocus={() => setGstFocused(true)} onBlur={() => setGstFocused(false)}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
            <label style={{ display: "block", fontSize: 10, fontWeight: 600, color: B.textMid, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: fonts.body }}>GST Number</label>
            {!readOnly && (
              <button
                type="button"
                onClick={() => { set("gst_number", "URP"); setGstError(getGSTValidationMessage("URP")); }}
                style={{ background: "none", border: "none", color: B.orange, textDecoration: "underline", cursor: "pointer", fontSize: 11, fontFamily: fonts.body, padding: 0 }}
              >
                URP
              </button>
            )}
          </div>
          <input
            value={form.gst_number || ""}
            onChange={e => { set("gst_number", e.target.value.toUpperCase()); setGstError(getGSTValidationMessage(e.target.value)); }}
            disabled={readOnly}
            style={{ background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "10px 13px", color: B.text, fontSize: 13, outline: "none", boxSizing: "border-box", width: "100%", fontFamily: fonts.body }}
          />
          {!readOnly && gstFocused && <GSTSegmentHint value={form.gst_number} />}
          {gstError && !readOnly && (
            <div style={{ fontSize: 11, color: B.red, marginTop: 4, fontFamily: fonts.body }}>{gstError}</div>
          )}
        </div>
        <div>
          <TransportSelect
            label="Transport"
            labelStyle={{ fontSize: 10, fontWeight: 600, color: B.textMid, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: fonts.body }}
            transports={transports}
            value={{ id: form.transport_id || "", name: form.transport_name || "", gst: form.transport_gst || "" }}
            onChange={({ id, name, gst }) => { set("transport_id", id); set("transport_name", name || ""); set("transport_gst", gst || ""); }}
            disabled={readOnly}
          />
        </div>
        <Input label="Address" value={form.address} onChange={e => set("address", toTitleCase(e.target.value))} disabled={readOnly} />
        <div>
          <label style={{ display: "block", fontSize: 10, fontWeight: 600, color: B.textMid, marginBottom: 8, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: fonts.body }}>
            Status
          </label>
          <select
            value={form.status || "active"}
            onChange={e => set("status", e.target.value)}
            disabled={readOnly}
            style={{ background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 12, color: B.text, fontFamily: fonts.body, outline: "none", width: "100%" }}
          >
            <option value="active">active</option>
            <option value="inactive">inactive</option>
            <option value="deleted">deleted</option>
          </select>
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 6 }}>
        <Btn onClick={onDone} variant="ghost">Close</Btn>
        {!readOnly && (
          <Btn onClick={handleSubmit} disabled={saving} variant="primary">
            {saving ? "Saving..." : initial ? "Save Customer" : "Add Customer"}
          </Btn>
        )}
      </div>
    </div>
  );
}
